import { createRouter, createWebHistory } from '@ionic/vue-router';
import { RouteRecordRaw } from 'vue-router';
import UnauthenticatedTabs from "@/views/UnauthenticatedTabs.vue";
import AuthenticatedTabs from "@/views/AuthenticatedTabs.vue";

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    component: () => import('@/views/UnknownView.vue')
  },
  // Public RCS/SMS messaging compliance pages (served on oscodocs.com AND
  // autoquipdocs.com; brand auto-detected by hostname). Required for RCS agent
  // registration / vetting.
  {
    path: '/opt-in',
    component: () => import('@/views/messaging/OptInView.vue')
  },
  {
    path: '/opt-out',
    component: () => import('@/views/messaging/OptOutView.vue')
  },
  {
    path: '/privacy',
    component: () => import('@/views/messaging/PrivacyPolicyView.vue')
  },
  {
    path: '/terms',
    component: () => import('@/views/messaging/MessagingTermsView.vue')
  },
  {
    // 3D machine configurator (scissor, VRC and ram lift product lines).
    // Lazy route: three.js ships only in this chunk, never on other pages.
    path: '/configurator',
    component: () => import('@/views/ConfigPocView.vue')
  },
  {
    // Virtual Showroom (Matt, 2026-07-29): a camera tour of up to six
    // machines in one room, fed by ?m=<encoded configurator query> params
    // (OSCO Connect's Showroom tab builds the links). Same lazy-chunk rule.
    path: '/showroom',
    component: () => import('@/views/ShowroomView.vue'),
    meta: { title: 'Virtual Showroom' }
  },
  {
    // Applications Display (Julie's showroom recommendations, 2026-09-11):
    // an MHI-style facility, one machine per product category, click a zone
    // to step in and configure. Separate from /showroom on purpose — that
    // room is live in front of customers. Same lazy-chunk rule.
    path: '/applications-display',
    component: () => import('@/views/ApplicationsDisplayView.vue'),
    meta: { title: 'Autoquip Applications Display' }
  },
  {
    // Find My Lift (Julie's showroom recommendations, 2026-09-11): the same
    // MHI-style facility organised by product category — Julie's version,
    // kept alongside /applications-display (Steve's custom-build
    // applications) in its own files (Matt, 2026-09-14). Same lazy-chunk rule.
    path: '/find-my-lift',
    component: () => import('@/views/FindMyLiftView.vue'),
    meta: { title: 'Autoquip Find My Lift' }
  },
  {
    // IRIS work platform concept (Matt, 2026-08-03): standalone demo of an
    // aerospace service lift whose steel iris hugs an upright rocket. Same
    // lazy-chunk rule — three.js stays in the /iris chunk.
    path: '/iris',
    component: () => import('@/views/IrisView.vue'),
    meta: { title: 'IRIS Work Platform' }
  },
  {
    // A customer's PROPOSAL (Matt, 2026-07-29): the same Virtual Showroom,
    // holding only the machines a salesperson put in front of this customer.
    // The short uid is allocated by OSCO Connect; the page reads the playlist
    // from /noauth/autodocs/proposal/:uid. Deliberately under /configurator
    // so a customer who is sent one link stays in one place.
    path: '/configurator/p/:uid',
    component: () => import('@/views/ShowroomView.vue'),
    meta: { title: 'Autoquip Proposal' }
  },
  {
    // JAB Becker compressor configurator (EN/DE). Same lazy-chunk rule:
    // three.js only inside this route's chunk.
    path: '/compressor-configurator',
    component: () => import('@/views/CompressorConfiguratorView.vue'),
    meta: { title: 'JAB Compressor Configurator' }
  },
  {
    // OSCO Controls 9ECP control-panel configurator (same lazy-chunk rule).
    path: '/control-configurator',
    component: () => import('@/views/ControlConfiguratorView.vue'),
    meta: { title: 'OSCO Control Panel Configurator' }
  },
  {
    path: '/ro/:id',
    component: () => import('@/views/ReadOnlyView.vue')
  },
  {
    // Bare machine simulation for embedding (OSCO Connect iframes this).
    // Lazy: three.js ships only in the simulation chunk.
    path: '/sim/:id',
    component: () => import('@/views/SimView.vue'),
    meta: { title: 'Machine Simulation' }
  },
  {
    // Unified customer-facing shopping cart. A cart may optionally
    // reference a machine; either way it renders here, not in ReadOnlyView.
    path: '/cart/:token',
    name: 'cart',
    component: () => import('@/views/CartView.vue')
  },
  {
    path: '/qs/:id',
    component: () => import('@/views/QuoteSurveyView.vue')
  },
  {
    // "How did we do?" survey linked from service-agent reply emails.
    // Star links carry ?stars=N and auto-submit on arrival (no sign-in).
    path: '/feedback/:requestId',
    name: 'service-feedback',
    component: () => import('@/views/ServiceFeedbackView.vue'),
    meta: { title: 'How Did We Do?' }
  },
  {
    path: '/loa/:id',
    component: () => import('@/views/LargeOrderAnnouncementView.vue')
  },
  {
    // Weekly factory-news questionnaire (J.A.B. / OSCO contacts) linked from
    // the Wednesday email
    path: '/weekly/:id',
    component: () => import('@/views/FactoryUpdateView.vue'),
    meta: { title: 'Weekly Factory Update' }
  },
  {
    path: '/c/',
    component: UnauthenticatedTabs,
    meta: { title: 'Deliveries' },
    children: [
      {
        path: '',
        redirect: '/c/unknown'
      },
      {
        path: 'd/:id',
        component: () => import('@/views/DeliveryView.vue')
      },
      {
        path: 'di/:id',
        component: () => import('@/views/DeliveryItem.vue')
      },
      {
        path: 'dk/:id',
        component: () => import('@/views/DeliveryKit.vue')
      },
      {
        path: 'h/:id',
        component: () => import('@/views/HistoryView.vue')
      },
      {
        path: 'c/:id',
        component: () => import('@/views/CommentsView.vue')
      },
      {
        path: 'sv/:id',
        component: () => import('@/views/ServiceView.vue')
      },
      {
        path: 'unknown',
        component: () => import('@/views/UnknownView.vue')
      }
    ]
  },
  {
    path: '/a/',
    component: AuthenticatedTabs,
    children: [
      {
        path: '',
        redirect: '/c/unknown'
      },
      {
          path: 'sv/conversation/:id',
          name: 'sv/conversation',
          component: () => import('@/views/ServiceConversationView.vue')
      },
      {
        path: 'sv/new/conversation/:id',
        name: 'sv/new/conversation',
        component: () => import('@/views/CreateServiceConversationView.vue')
      },
      {
        path: 'cu/:id',
        name: 'cu',
        component: () => import('@/views/CustomerUploadView.vue')
      },
      {
        path: 'ai/',
        name: 'ai',
        component: () => import('@/views/AILabView.vue')
      },
      {
        // Autoquip Deliveries: dashboard of outbound packs needing photos.
        path: 'deliveries',
        name: 'deliveries',
        component: () => import('@/views/AqDeliveryDashboardView.vue'),
        meta: { title: 'Delivery Photos' }
      },
      {
        // Autoquip Deliveries: shipper photo flow. 'ship/pack' before
        // 'ship/:partNum' so 'pack' is never read as a part number.
        path: 'ship/pack/:uuid',
        name: 'ship/pack',
        component: () => import('@/views/AqShipmentChecklistView.vue'),
        meta: { title: 'Delivery Photos' }
      },
      {
        path: 'ship/:partNum',
        name: 'ship',
        component: () => import('@/views/AqShipperView.vue'),
        meta: { title: 'Delivery Photos' }
      }
    ]
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

// Per-feature browser tab title; index.html's <title>AutoDocs</title> is the default.
router.afterEach((to) => {
  const title = to.matched.map((record) => record.meta.title).filter(Boolean).pop() as string | undefined;
  document.title = title || 'AutoDocs';
});

export default router
